import React, { FC, useContext, useEffect, useMemo, useState } from 'react';

import { RenderElementProps } from 'slate-react';

import ErrorSnackBar from '../../../../components/errors/ErrorSnackBar';
import useVariantsByComponentIdQuery from 'hooks/components/useVariantsByComponentIdQuery';
import styled from 'styled-components';

//constants
import aampeTheme from '../../../../constants/aampeTheme';

//context
import { EditorContext } from '../../../../context/EditorContext';
// context
import { VariantSelectionContext } from '../../../../context/VariantSelectionContext';

//helpers
import debounce from '../../../../helpers/debounce';

import useComponentTypesQuery from '../../../../hooks/component-types/useComponentTypesQuery';
import { useLockFormulaWarning } from '../../../../hooks/formulas/useLockFormulaWarning';
import useRoleFlags from 'hooks/auth/useRoleFlags';

import { VariantElement } from '../../../../models/editor.model';

import WarningIconComponent from '../WarningIconComponent';
import SlateVariantToolbarComponent from './SlateVariantToolbar';
import VariantCreationInput from './VariantCreationInput';
// components
import ZeroWidthSpace from './ZeroWidthSpace';

interface StyleProps {
    componentPrimaryColor: string;
    selectedTextColor: string;
    isFocused: boolean;
    isSelected: boolean;
}

type VariantElementProps = RenderElementProps & {
    element: VariantElement;
    selectedElement?: { [key: string]: any };
    formulaId: number;
    setEditingVariant: (value: boolean) => void;
    setAlternateCreateError: (value: string | null) => void;
};

const VariantElementComponent: FC<
    React.PropsWithChildren<VariantElementProps>
> = ({
    element,
    attributes,
    children,
    selectedElement,
    formulaId,
    setEditingVariant,
    setAlternateCreateError,
}) => {
    const {
        editorDetails: { isPreviewError },
    } = useContext(EditorContext);
    const { hasWriteAccess } = useRoleFlags();
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [showToolBar, setShowToolBar] = useState<boolean>(false);

    const { data = [], isLoading } = useVariantsByComponentIdQuery(element.id);

    const { hasWarning, labelWarningComponents } = useLockFormulaWarning();

    const [errorSnackBar, setErrorSnackBar] = useState<boolean>(
        isPreviewError && hasWarning,
    );

    const displayText = useMemo<string>(() => {
        if (!isLoading && data.length) {
            return data[0].content;
        }
        return '[Component]';
    }, [data, element, isLoading]);

    const { data: componentTypes } = useComponentTypesQuery();

    const componentPrimaryColor = useMemo<string>(() => {
        return componentTypes?.map[element.variantType]?.text_color ?? '';
    }, [componentTypes, element]);

    const selectedTextColor = useMemo<string>(() => {
        return (
            componentTypes?.map[element.variantType]?.selected_text_color ??
            aampeTheme.colors.white
        );
    }, [componentTypes, element]);

    const onMouseChange = (showTooltip: boolean) => {
        setShowToolBar(showTooltip);
    };

    const selected = selectedElement
        ? selectedElement.elementId === element.id
        : false;

    useEffect(() => {
        if (selected) {
            setIsFocused(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setIsFocused]);

    const showWarningIcon = useMemo<boolean>(() => {
        if (!hasWarning) {
            return false;
        }
        return labelWarningComponents[element.variantType as string] ?? false;
    }, [element, labelWarningComponents, hasWarning]);

    const handleClose = debounce(() => {
        setErrorSnackBar(false);
    }, 2000);

    return (
        <VariantSelectionContext.Provider value={{ parentSelected: selected }}>
            <span {...attributes}>
                <ZeroWidthSpace />
                <Variant
                    id={element.id}
                    onMouseEnter={() => onMouseChange(true)}
                    onMouseLeave={() => onMouseChange(false)}
                    tabIndex={0}
                    componentPrimaryColor={componentPrimaryColor}
                    selectedTextColor={selectedTextColor}
                    isFocused={isFocused}
                    isSelected={selected}
                >
                    <ZeroWidthSpace />
                    {displayText}
                    {children}
                </Variant>
                {showWarningIcon && (
                    <WarningIconComponent type={element.variantType} />
                )}
                {isFocused && (
                    <VariantCreationInput
                        element={element}
                        isFocused={isFocused}
                        setEditingVariant={setEditingVariant}
                        setIsFocused={setIsFocused}
                        selectedElement={selectedElement}
                        formulaId={formulaId}
                        setAlternateCreateError={setAlternateCreateError}
                    />
                )}
                {hasWriteAccess && (
                    <SlateVariantToolbarComponent
                        element={element}
                        isHovered={showToolBar}
                        onMouseChange={onMouseChange}
                        onClick={() => setIsFocused(true)}
                    />
                )}
            </span>

            <ErrorSnackBar
                title="Locked components need matching labels. Unlock to enable label pairing with applied learning or to pair alternates  by label."
                handleClose={handleClose}
                open={errorSnackBar}
            />
        </VariantSelectionContext.Provider>
    );
};

export default VariantElementComponent;

const Variant = styled.span.withConfig({
    shouldForwardProp: (prop) =>
        ![
            'isSelected',
            'isFocused',
            'variantType',
            'componentPrimaryColor',
            'selectedTextColor',
        ].includes(prop), // Prevent forwarding these custom props
})<StyleProps>(
    ({ isFocused, isSelected, componentPrimaryColor, selectedTextColor }) => ({
        outline: 'none',
        display: isFocused ? 'none' : 'inline-block',
        fontSize: isSelected ? aampeTheme.size.font.default : '',
        fontWeight: isSelected ? 400 : 500,
        userSelect: isFocused ? 'none' : 'auto',
        lineHeight: isSelected ? '24px' : '22px',
        minWidth: '10px',
        color:
            isSelected && componentPrimaryColor
                ? selectedTextColor
                : componentPrimaryColor,
        backgroundColor: isSelected
            ? componentPrimaryColor || 'inherit'
            : 'inherit',
        borderRadius: isSelected ? aampeTheme.borderRadius.default : '',
        borderBottom: isSelected ? 'none' : `1px solid`,
        cursor: 'pointer',
        padding: isSelected ? aampeTheme.spacing4(0, 1) : '',
        ...(isFocused && { width: '200px' }),
    }),
);
